/**
 * Referral-code CAPTURE — the client half of the referral flywheel. An invite
 * link carries `?ref=<code>`; this reads it off `location.search`, validates it,
 * and REMEMBERS it so the code survives everything that happens between landing
 * and signing up: the hash router writing `#/c/<id>` (which does not touch the
 * query), a reload, the login⇄signup toggle, and the age gate.
 *
 * Without this the whole chain below it is dead: `AuthGate → AuthSurface →
 * SignupWizard` all plumb a `referralCode` prop, and `POST /auth/register` accepts
 * it — but nothing ever supplied one, so every invited signup was attributed to
 * nobody. Pure + storage-injected (DIP) so it is unit-tested with no DOM.
 *
 * Zero domain logic: qualification, the 5-signup bonus, and rev-share are all
 * decided server-side. This only carries the code to the register call.
 */

/** The query parameter an invite link carries. */
export const REFERRAL_PARAM = 'ref';

/** Where a captured code is remembered between landing and sign-up. */
export const REFERRAL_STORAGE_KEY = '4f:ref';

/**
 * Referral codes are server-minted opaque handles. Accept only a conservative
 * charset + length so a hostile link can never smuggle markup or an oversized
 * body field into the register call (the edge re-validates; this is defence in
 * depth at the entry point).
 */
const CODE_PATTERN = /^[A-Za-z0-9_-]{4,64}$/;

/**
 * Extract a valid referral code from a `location.search` string (with or without
 * the leading `?`), or `null`. Never throws on a malformed query.
 */
export function readReferralCode(search: string): string | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search.replace(/^\?/, ''));
  } catch {
    return null;
  }
  const raw = params.get(REFERRAL_PARAM)?.trim();
  if (!raw || !CODE_PATTERN.test(raw)) {
    return null;
  }
  return raw;
}

/** The persistence seam for a captured code (DIP over `localStorage`). */
export interface ReferralCodeStore {
  get(): string | null;
  set(code: string): void;
  clear(): void;
}

/** The default `localStorage`-backed store; a no-op when storage is unavailable. */
export function browserReferralStore(): ReferralCodeStore {
  return {
    get() {
      try {
        return globalThis.localStorage?.getItem(REFERRAL_STORAGE_KEY) ?? null;
      } catch {
        return null;
      }
    },
    set(code) {
      try {
        globalThis.localStorage?.setItem(REFERRAL_STORAGE_KEY, code);
      } catch {
        // Storage disabled (private mode) — the code still rides this page load.
      }
    },
    clear() {
      try {
        globalThis.localStorage?.removeItem(REFERRAL_STORAGE_KEY);
      } catch {
        // Nothing to clear when storage is unavailable.
      }
    },
  };
}

/**
 * The capture entry point: a code in the URL wins (and is remembered), otherwise
 * fall back to a previously remembered one. Returns `null` when there is neither,
 * so an organic signup sends no `referralCode` at all.
 */
export function captureReferralCode(search: string, store: ReferralCodeStore): string | null {
  const fromUrl = readReferralCode(search);
  if (fromUrl) {
    store.set(fromUrl);
    return fromUrl;
  }
  return store.get();
}
